import { Alert, Box, Button, LinearProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditNoteIcon from '@mui/icons-material/EditNote';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useNotification } from '../../../hooks/NotificationContext';
import { useUser } from '../../../context/UserContext';
import {
  addTestCases,
  generateAITestCases,
  generateAITestCasesFromStory,
  generateAITestCasesFromContent,
  generateAITestCasesFromContentFile,
} from '../../../modules/jira';
import { getProjectAIAccess } from '../../../modules/aiConfig';
import { addTestSteps } from '../../../modules/teststep';
import { addComment } from '../../../modules/entity-details';
import ConfirmationModal from '../../elements/ConfirmationModal';
import Modal from '../../elements/Modal';
import CreateTestCaseForm from './CreateTestCaseForm';
import JiraReviewScreen from './ai/JiraReviewScreen';
import InMemoryStepsModal from './ai/InMemoryStepsModal';
import AiInputScreen from './ai/AiInputScreen';
import AiSuggestions from './ai/AiSuggestions';
import { defaultFormData, SAVE_PHASE } from '../test-cycles/static-data';
import { useBulkSave } from './ai/Usebulksave';
import { useProject } from '../../../context/ProjectContext';
import QUERY_KEYS from '../../../query/queryKeys';
import { createWeblink } from '../../../modules/traceability';
import { apiRoot } from "../../../modules/callApi";
import AiFeedbackModal from '../../../features/wizards/AiFeedbackModal';
import { useAccess } from '../../../hooks/useAccess';

const normalizeStep = ({ step, testData, expectedResult, uri }) => ({
  id: null, step: step || '', testData: testData || '', expectedResult: expectedResult || '', uri: uri || '',
});

const mapApiTcToInternal = (tc, idx, testsuites) => ({
  ...defaultFormData,
  _id: idx,
  selected: false,
  summary: tc.summary || '',
  objective: tc.objective || '',
  preCondition: tc.pre_condition || '',
  module: tc.module || null,
  labels: tc.labels || [],
  technologyLayer: tc.technology_layer || '',
  coverageTag: tc.coverage_tag || 'Happy Path',
  testsuites,
  status: tc.status || defaultFormData.status,
  priority: tc.priority || defaultFormData.priority,
  owner: tc.owner || defaultFormData.owner,
  reviewer: tc.reviewer || null,
  isAutomationNeeded: tc.automation_candidate === 'strong' ? true : defaultFormData.isAutomationNeeded,
  automationStatus: tc.automation_candidate === 'strong' ? 'To Be Planned' : (tc.automationStatus || defaultFormData.automationStatus),
  testSteps: (tc.test_steps || []).map((s) => ({
    step: s.step || '', testData: s.test_data || '', expectedResult: s.expected_result || '', uri: '',
  })),
  stepsExpanded: false,
  _crossMatchKey: tc.cross_match_key ?? null,
  _crossMatchScore: tc.cross_match_score ?? null,
  _crossMatchStrength: tc.cross_match_strength ?? null,
  _automationCandidate: tc.automation_candidate ?? null,
});

const mapSingleTcToForm = (result) => ({
  summary: result.summary || '',
  objective: result.objective || '',
  preCondition: result.pre_condition || '',
  technologyLayer: result.technology_layer || '',
  module: result.module || null,
  labels: result.labels || [],
  isAutomationNeeded: result.automation_candidate === 'strong' ? true : defaultFormData.isAutomationNeeded,
  automationStatus: result.automation_candidate === 'strong' ? 'To Be Planned' : defaultFormData.automationStatus,
});

const GeneratingOverlay = ({ subtitle }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 3, minHeight: 260 }}>
    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', borderColor: 'primary.200' }}>
      <AutoAwesomeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Generating test cases…</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
    <Box sx={{ width: '100%', maxWidth: 320 }}>
      <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
    </Box>
    <Typography variant="caption" color="text.secondary">Please don't close this window</Typography>
  </Box>
);

const WizardStepper = ({ steps, activeStep }) => (
  <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
    <Stepper activeStep={activeStep} alternativeLabel>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '12px', fontWeight: 500 } }}>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  </Box>
);

const ChoiceScreen = ({ hasAiAccess, aiAccessDeniedReason, onChooseAi, onChooseManual }) => {
  const cards = [
    {
      id: 'ai',
      icon: <AutoAwesomeIcon sx={{ fontSize: 36 }} />,
      title: 'Generate with AI',
      description: 'Describe a scenario, use a Jira story, paste requirements, or upload a document',
      locked: !hasAiAccess,
      lockReason: aiAccessDeniedReason || 'AI not available',
      onClick: onChooseAi,
    },
    {
      id: 'manual',
      icon: <EditNoteIcon sx={{ fontSize: 36 }} />,
      title: 'Write manually',
      description: 'Fill in all test case fields yourself',
      locked: false,
      onClick: onChooseManual,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, py: 4, px: 2 }}>
      <Box>
        <Typography variant="h6" fontWeight={600} textAlign="center">How would you like to create this test case?</Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>Choose a method to get started</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        {cards.map(({ id, icon, title, description, locked, lockReason, onClick }) => (
          <Box
            key={id}
            role="button"
            tabIndex={locked ? -1 : 0}
            onClick={() => !locked && onClick()}
            onKeyDown={(e) => { if (!locked && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
              p: 3, width: 210, borderRadius: 2, border: '2px solid',
              borderColor: locked ? 'divider' : 'primary.main',
              cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.65 : 1, outline: 'none',
              color: locked ? 'text.disabled' : 'text.primary', transition: 'all 0.15s ease',
              '&:hover': locked ? {} : { bgcolor: 'primary.50', boxShadow: 3, transform: 'translateY(-2px)' },
              '&:focus-visible': { boxShadow: '0 0 0 3px #3b82f6' },
            }}
          >
            <Box sx={{ color: locked ? 'text.disabled' : 'primary.main' }}>{icon}</Box>
            <Typography variant="subtitle1" fontWeight={600} textAlign="center">{title}</Typography>
            <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ lineHeight: 1.5 }}>{description}</Typography>
            {locked && <Typography variant="caption" color="primary.main" fontWeight={500} textAlign="center">{lockReason}</Typography>}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Screen constants ──────────────────────────────────────────────────────────

const AI_STEPS = ['Input', 'Generating', 'Review & Save'];
const MANUAL_STEPS = ['Details', 'Save'];

// ── AddTestCaseModal ──────────────────────────────────────────────────────────

const AddTestCaseModal = ({
  showModal, handleClose,
  statuses, priorities, modules, labels, testSuites, projectUsers,
  loading, projectId, isReviewWorkflow, refetchData, technologyLayers,
  initialMode = 'choice',
  postFeedbackAction = 'navigate-highlight',
}) => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { currentUser } = useUser();
  const currentUserId = currentUser?.id ?? null;

  // ── Navigation state ──────────────────────────────────────────────────────
  const [mode, setMode] = useState(initialMode);   // 'choice' | 'ai' | 'manual'
  const [aiScreen, setAiScreen] = useState('input');    // 'input' | 'generating' | 'review' | 'edit'

  // ── AI input state ────────────────────────────────────────────────────────
  const [textInput, setTextInput] = useState('');
  const [aiQuantity, setAiQuantity] = useState('multiple');
  const [sourceMode, setSourceMode] = useState('text');
  const [aiError, setAiError] = useState('');
  const [genSubtitle, setGenSubtitle] = useState('');

  const [aiPayload, setAiPayload] = useState({payload: null, canGenerate: false});

  // ── Form state ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState(defaultFormData);
  const [aiTestSteps, setAiTestSteps] = useState([]);
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiFormSource, setAiFormSource] = useState(null);
  const [suggestedLabels, setSuggestedLabels] = useState([]);
  const [suggestedModule, setSuggestedModule] = useState(null);
  const [suggestedTechLayer, setSuggestedTechLayer] = useState(null);

  // ── Review state ──────────────────────────────────────────────────────────
  const [reviewTCs, setReviewTCs] = useState([]);
  const [editingTcId, setEditingTcId] = useState(null);
  const [existingMatches, setExistingMatches] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState(new Set());
  const [tokenUsage, setTokenUsage] = useState(null);
  const [selectedTestSuites, setSelectedTestSuites] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTcIds, setFeedbackTcIds] = useState([]);
  const [modalHidden, setModalHidden] = useState(false);
  const selectedStoryRef = useRef(null);

  // ── Step state lifted here so the footer re-renders when step changes ──────
  const [createStep, setCreateStep] = useState(1);
  const TOTAL_STEPS = 3;
  const isLastStep = createStep === TOTAL_STEPS;

  const validateAllAndSubmitRef = useRef(null);
  const { canAccess } = useAccess();

  // ── Confirmation modal ────────────────────────────────────────────────────
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmContext, setConfirmContext] = useState(null);

  const uploadedFileRef = useRef(null);
  const confluenceLinkRef = useRef(null);

  const { currentProject } = useProject();
  const linkedJiraProjects = currentProject?.linked_jira_projects || [];

  const { data: aiAccess } = useQuery({
    queryKey: QUERY_KEYS.project.aiAccess(projectId, 'testCaseGeneration'),
    queryFn: () => getProjectAIAccess(projectId, 'testCaseGeneration'),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const hasAiAccess = !!aiAccess?.has_access;
  const aiAccessDeniedReason = aiAccess?.message ?? null;

  const { savePhase, setSavePhase, saveProgress, saveResult, setSaveResult, handleBulkSave, resetSaveState } = useBulkSave({
    jiraReviewTCs: reviewTCs, setJiraReviewTCs: setReviewTCs,
    selectedStory: selectedStoryRef.current,
    statuses, priorities, currentUserId, projectUsers,
    projectId, refetchData, handleClose, selectedExisting, existingMatches,
    uploadedFileRef,
    confluenceLinkRef,
    onSaveSuccess: ({ savedIds, savedKeys }) => {
      setModalHidden(true);   // hide main modal; keep component mounted so feedback state lives
      setFeedbackTcIds(savedIds);
      savedKeysRef.current = savedKeys ?? [];
      setFeedbackOpen(true);
    },
  });

  // ── Derived ───────────────────────────────────────────────────────────────
  const isEditingTc = editingTcId !== null;
  const selectedTcs = reviewTCs.filter((tc) => tc.selected);
  const hasUnresolvedWarnings = selectedTcs.some((tc) => !tc.technologyLayer);
  const isBulkActive = savePhase === SAVE_PHASE.SAVING || savePhase === SAVE_PHASE.LINKING;
  const isInFlight = aiScreen === 'generating' || submitting || isBulkActive;
  const currentTechLayerName = technologyLayers?.find((t) => t.id === formData.technologyLayer)?.name || '';
  const filteredStatuses = useMemo(() => statuses?.filter((s) => s.name !== 'Archived') ?? [], [statuses]);
  const isAdmin = canAccess('ManageTestSuite');

  const stepperSteps = mode === 'manual' ? MANUAL_STEPS : AI_STEPS;
  const stepperActive = useMemo(() => {
    if (mode === 'manual') return 0;
    if (mode === 'ai') {
      if (aiScreen === 'input') return 0;
      if (aiScreen === 'generating') return 1;
      return 2;
    }
    return 0;
  }, [mode, aiScreen]);

  // ── Reset ─────────────────────────────────────────────────────────────────
  const resetAll = useCallback(() => {
    resetSaveState();
    setMode(initialMode);
    setAiScreen('input');
    setTextInput('');
    setAiQuantity('multiple');
    setSourceMode('text');
    setAiError('');
    setGenSubtitle('');
    setFormData(defaultFormData);
    setAiTestSteps([]);
    setShowStepsModal(false);
    setSubmitting(false);
    setAiFormSource(null);
    setSuggestedLabels([]);
    setSuggestedModule(null);
    setSuggestedTechLayer(null);
    setReviewTCs([]);
    setEditingTcId(null);
    setExistingMatches([]);
    setSelectedExisting(new Set());
    setTokenUsage(null);
    setSelectedTestSuites([]);
    selectedStoryRef.current = null;
    setAiPayload({ payload: null, canGenerate: false });
    setCreateStep(1);
    validateAllAndSubmitRef.current = null;
    setShowConfirm(false);
    setConfirmContext(null);
  }, [resetSaveState, initialMode]);

  const prevShow = useRef(showModal);
  useEffect(() => {
    if (prevShow.current && !showModal) resetAll();
    prevShow.current = showModal;
  }, [showModal, resetAll]);

  // ── Apply review result (multiple TCs) ───────────────────────────────────
  const applyReviewResult = useCallback((result, suites) => {
    const tcs = (result?.test_cases || []).map((tc, idx) => mapApiTcToInternal(tc, idx, suites));
    if (!tcs.length) return false;
    setReviewTCs(tcs);
    setTokenUsage(result?.token_usage ?? null);
    setExistingMatches(result?.existing_matches || []);
    setSelectedExisting(new Set());
    return true;
  }, []);

  // ── Apply single TC result (edit screen) ──────────────────────────────────
  const applySingleResult = useCallback((result, suites) => {
    const mapped = mapSingleTcToForm(result);
    setFormData((prev) => ({ ...prev, ...mapped, testsuites: suites }));
    setAiTestSteps((result.test_steps || []).map(({ step, test_data, expected_result }) => ({
      step: step || '', testData: test_data || '', expectedResult: expected_result || '', uri: '',
    })));
    setSuggestedLabels(result.labels || []);
    setSuggestedModule(result.module || null);
    setSuggestedTechLayer(result.technology_layer || null);
    setTokenUsage(result?.token_usage ?? null);
    setAiFormSource('scenario');
  }, []);

  // ── Generate handler ──────────────────────────────────────────────────────
  const handleGenerate = useCallback(async ({ description, mode: genMode, content, file, story, epicContext, confluencePages }) => {
    setAiError('');
    setAiScreen('generating');
    selectedStoryRef.current = story ?? null;
    setGenSubtitle(story ? `Analysing ${story.key} and creating test cases` : 'AI is generating your test cases');
    uploadedFileRef.current = file ?? null;
    confluenceLinkRef.current = confluencePages?.[0]?.url ?? null;

    try {
      // Single TC — all modes go to edit screen
      if (genMode === 'single') {
        let result;
        if (story) {
          result = await generateAITestCasesFromStory(
            { key: story.key, summary: story.summary, description: story.description, issuestatus: story.issuestatus, priority: story.priority },
            projectId, epicContext ?? null, confluencePages ?? null, 'single',
          );
        } else if (file) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('projectId', projectId);
          fd.append('quantity', 'single');
          result = await generateAITestCasesFromContentFile(fd);
        } else if (content) {
          result = await generateAITestCasesFromContent({ projectId, content, quantity: 'single' });
        } else {
          result = await generateAITestCases({ scenario: description, projectId, quantity: 'single' });
        }
        applySingleResult(result, selectedTestSuites);
        setAiScreen('edit');
        return;
      }

      // Multiple TCs — all modes go to review screen
      let result;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('projectId', projectId);
        fd.append('quantity', 'multiple');
        result = await generateAITestCasesFromContentFile(fd);
      } else if (story) {
        result = await generateAITestCasesFromStory(
          { key: story.key, summary: story.summary, description: story.description, issuestatus: story.issuestatus, priority: story.priority },
          projectId, epicContext ?? null, confluencePages ?? null, 'multiple',
        );
      } else if (content) {
        result = await generateAITestCasesFromContent({ projectId, content, quantity: 'multiple' });
      } else {
        result = await generateAITestCases({ scenario: description, projectId, quantity: 'multiple' });
      }

      if (!applyReviewResult(result, selectedTestSuites)) {
        setAiError(`No test cases were generated because the provided input doesn't contain enough information. Please add more details and try again.`);
        setAiScreen('input');
      } else {
        setAiScreen('review');
      }
    } catch (error) {
      setAiError(error.message || 'Something went wrong. Please try again.');
      setAiScreen('input');
    }
  }, [projectId, selectedTestSuites, applyReviewResult, applySingleResult]);

  const handleFooterGenerate = useCallback(() => {
    if (aiPayload.payload) {
      handleGenerate(aiPayload.payload);
    }
  }, [aiPayload, handleGenerate]);

  // ── Manual form submit ────────────────────────────────────────────────────
  const handleFormSubmit = useCallback(async () => {
    if (!formData.summary?.trim()) { addNotification('danger', 'Please add a summary'); return; }
    setSubmitting(true);
    try {
      const testcase = { ...formData, reasonForNotAutomated: formData.isAutomated === 'No' ? formData.reasonForNotAutomated : '' };
      const response = await addTestCases({ testcase }, projectId);
      const tcId = response?.test_case?.id;
      const tcKey = response?.test_case?.key;
      if (aiTestSteps.length > 0) {
        await addTestSteps({ testSteps: aiTestSteps.map(normalizeStep) }, tcId)
          .catch((err) => console.error('Failed to save test steps:', err));
      }

      if (mode === 'ai') {
        try {
          await addComment({
            comment: {
              body: 'This test case was generated by AI.',
              entityType: 'TestCase',
              entityId: tcId,
              mentions: [],
              additionalData: {},
            },
          }, projectId);
        } catch (err) {
          console.error('Failed to add AI comment for TC:', tcKey, err);
        }
      }

      const uploadedFile = uploadedFileRef.current;
      if (uploadedFile) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('files', uploadedFile);
          formDataUpload.append('entityType', 'TestCase');
          formDataUpload.append('entityId', tcId);
          await fetch(`${apiRoot}attachments?projectId=${projectId}`, {
            method: 'POST',
            body: formDataUpload,
            headers: { Authorization: localStorage.getItem('token') },
          });
        } catch (err) { console.error('Failed to upload attachment:', err); }
      }

      const confluenceLink = confluenceLinkRef.current;
      if (confluenceLink) {
        try {
          await createWeblink({
            weblink: {
              entityType: 'TestCase',
              entityId: tcId,
              url: confluenceLink,
              description: 'Confluence link from Jira story',
            },
          });
        } catch (err) { console.error('Failed to save weblink:', err); }
      }

      const story = selectedStoryRef.current;
      if (story && tcId && tcKey) {
        try {
          const { generateNotificationUrl } = await import('../../../lib/notificationUtils');
          const { linkIssueToBulkEntities } = await import('../../../modules/associations');
          await linkIssueToBulkEntities(
            {
              issue: {
                id: story.id,
                key: story.key,
                priority: story.priority || '',
                projectId: story.projectId,
              },
              entityIds: [{
                entityId: tcId,
                entityKey: tcKey,
                entityUrl: generateNotificationUrl('TestCase', projectId, tcKey, 'traceability', null),
              }],
              entityType: 'TestCase',
              gainssProjectId: projectId,
            },
            projectId,
          );
        } catch (err) { console.error('Failed to link test case to story:', story?.key, err); }
      }

      addNotification('success', 'Test case created successfully!');
      const tcKeyLocal = tcKey;
      if (mode === 'ai') {
        pendingNavRef.current = `/project/${projectId}/testcases/${tcKeyLocal}?type=testscripts`;
        setModalHidden(true);
        setFeedbackTcIds([tcId].filter(Boolean));
        setFeedbackOpen(true);
      } else {
        resetAll();
        navigate(`/project/${projectId}/testcases/${tcKeyLocal}?type=testscripts`);
      }
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to create test case. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }, [formData, projectId, aiTestSteps, mode, addNotification, navigate, resetAll]);

  // ── TC inline edit ────────────────────────────────────────────────────────
  const handleEditTc = useCallback((tc) => {
    setEditingTcId(tc._id);
    setFormData({
      ...defaultFormData,
      summary: tc.summary,
      objective: tc.objective,
      preCondition: tc.preCondition,
      module: tc.module,
      labels: tc.labels,
      technologyLayer: tc.technologyLayer,
      testsuites: tc.testsuites,
      status: tc.status,
      priority: tc.priority,
      owner: tc.owner,
      reviewer: tc.reviewer,
      country: tc.country,
      isAutomationNeeded: tc._automationCandidate === 'strong' ? true : (tc.isAutomationNeeded ?? defaultFormData.isAutomationNeeded),
      isAutomated: tc.isAutomated,
      automationStatus: tc.automationStatus,
      automatedBy: tc.automatedBy,
      automationMethod: tc.automationMethod,
      automationCountry: tc.automationCountry,
      notAutomatedCategory: tc.notAutomatedCategory,
      notAutomatedSubCategory: tc.notAutomatedSubCategory,
      reasonForNotAutomated: tc.reasonForNotAutomated,
      coverageTag: tc.coverageTag,
    });
    setAiTestSteps(tc.testSteps || []);
    setAiFormSource(null);
    setAiScreen('edit');
  }, []);

  const handleSaveEditedTc = useCallback(() => {
    setReviewTCs((prev) =>
      prev.map((tc) =>
        tc._id === editingTcId
          ? { ...tc, ...formData, testSteps: aiTestSteps }
          : tc,
      ),
    );
    setEditingTcId(null);
    setFormData(defaultFormData);
    setAiTestSteps([]);
    setAiFormSource(null);
    setAiScreen('review');
  }, [editingTcId, formData, aiTestSteps]);

  // ── Suggestion chips ──────────────────────────────────────────────────────
  const handleAcceptLabel = useCallback((id) => {
    setFormData((prev) => ({ ...prev, labels: prev.labels.includes(id) ? prev.labels : [...prev.labels, id] }));
    setSuggestedLabels((prev) => prev.filter((l) => l !== id));
  }, []);
  const handleAcceptModule = useCallback(() => {
    setSuggestedModule((mod) => { setFormData((prev) => ({ ...prev, module: mod })); return null; });
  }, []);
  const handleAcceptTechLayer = useCallback(() => {
    setSuggestedTechLayer((tech) => { setFormData((prev) => ({ ...prev, technologyLayer: tech })); return null; });
  }, []);

  // ── Close / discard guard ─────────────────────────────────────────────────
  const handleCloseModal = useCallback(() => {
    if (isInFlight) return;
    if ((mode === 'manual' || aiScreen === 'edit') && (formData.summary || formData.objective || formData.preCondition)) {
      setConfirmContext(isEditingTc ? 'edit' : 'form');
      setShowConfirm(true);
      return;
    }
    if (aiScreen === 'review' && reviewTCs.length > 0 && savePhase === SAVE_PHASE.IDLE) {
      setConfirmContext('review');
      setShowConfirm(true);
      return;
    }
    handleClose(); resetAll();
  }, [isInFlight, mode, aiScreen, formData.summary, formData.objective, formData.preCondition, isEditingTc, reviewTCs.length, savePhase, handleClose, resetAll]);

  const handleDiscard = useCallback(() => {
    setShowConfirm(false);
    if (confirmContext === 'edit') {
      setEditingTcId(null);
      setFormData(defaultFormData);
      setAiTestSteps([]);
      setAiFormSource(null);
      setAiScreen('review');
    } else {
      handleClose(); resetAll();
    }
    setConfirmContext(null);
  }, [confirmContext, handleClose, resetAll]);
  const pendingNavRef = useRef(null);
  const savedKeysRef = useRef([]);
  const handleFeedbackClose = useCallback(() => {
    setFeedbackOpen(false);
    setModalHidden(false);
    const dest = pendingNavRef.current;
    const keys = savedKeysRef.current;
    pendingNavRef.current = null;
    savedKeysRef.current = [];
    resetAll();
    handleClose();

    if (postFeedbackAction === 'refetch') {
      refetchData?.();
    } else if (dest) {
      navigate(dest);
    } else if (keys.length > 0) {
      navigate(`/project/${projectId}/testcases`, {
        state: { aiGeneratedKeys: keys },
      });
    }
  }, [resetAll, handleClose, navigate, postFeedbackAction, refetchData, projectId]);
  // ── Modal title ───────────────────────────────────────────────────────────
  const modalTitle = useMemo(() => {
    if (mode === 'choice' || mode === 'manual') return 'Create Test Case';
    if (aiScreen === 'edit') return isEditingTc ? 'Edit Test Case' : 'Create Test Case';
    if (aiScreen === 'review') return 'Review Generated Test Cases';
    return 'Generate with AI';
  }, [mode, aiScreen, isEditingTc]);

  // ── Step-aware footer for wizard screens (manual + ai/edit) ──────────────
  const renderWizardFooter = (finalLabel, onFinalClick, finalDisabled = false) => (
    <>
      {createStep === 1 ? (
        <Button
          variant="outlined"
          color="secondary"
          disabled={submitting}
          onClick={handleCloseModal}
        >
          Cancel
        </Button>
      ) : (
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          disabled={submitting}
          onClick={() => validateAllAndSubmitRef._goToPrevStep?.()}
        >
          Back
        </Button>
      )}

      {!isLastStep ? (
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => validateAllAndSubmitRef._goToNextStep?.()}
        >
          Next
        </Button>
      ) : (
        <Button
          variant="contained"
          disabled={finalDisabled || submitting}
          onClick={() => validateAllAndSubmitRef.current?.(onFinalClick)}
        >
          {finalLabel}
        </Button>
      )}
    </>
  );

  // ── Footer actions ────────────────────────────────────────────────────────
  const renderActions = () => {
    if (isInFlight || savePhase === SAVE_PHASE.SUCCESS) return null;

    if (mode === 'choice') {
      return (
        <Button variant="outlined" color="secondary" onClick={() => { handleClose(); resetAll(); }}>
          Cancel
        </Button>
      );
    }

    if (mode === 'manual') {
      return renderWizardFooter('Create', handleFormSubmit);
    }

    if (mode === 'ai') {
      const backToChoice = (
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => initialMode === 'ai' ? (handleClose(), resetAll()) : setMode('choice')}
        >
          {initialMode === 'ai' ? 'Cancel' : 'Back'}
        </Button>
      );

      if (aiScreen === 'input') {
        return (
          <>
            {backToChoice}
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleFooterGenerate}
              disabled={!aiPayload.canGenerate}
            >
              Generate
            </Button>
          </>
        );
      }

      if (aiScreen === 'review') {
        if (savePhase === SAVE_PHASE.PARTIAL || savePhase === SAVE_PHASE.ERROR) {
          return (
            <>
              <Button variant="outlined" color="secondary" onClick={() => { setSavePhase(SAVE_PHASE.IDLE); setSaveResult(null); }}>
                Retry Failed
              </Button>
              <Button variant="outlined" onClick={() => { refetchData(); resetAll(); handleClose(); }}>
                Close
              </Button>
            </>
          );
        }
        return (
          <>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => { setAiScreen('input'); setAiError(''); }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkSave}
              disabled={(selectedTcs.length === 0 && selectedExisting.size === 0) || hasUnresolvedWarnings}
              title={hasUnresolvedWarnings ? 'Resolve tech layer warnings to enable save' : undefined}
            >
              Save
            </Button>
          </>
        );
      }

      if (aiScreen === 'edit') {
        const finalLabel = isEditingTc ? 'Save Changes' : 'Create';
        const finalAction = isEditingTc ? handleSaveEditedTc : handleFormSubmit;
        return renderWizardFooter(finalLabel, finalAction);
      }
    }

    return null;
  };

  // ── Form content (manual + ai/edit) ──────────────────────────────────────
  const renderFormContent = (isEditing) => (
    <>
      {(isEditing || aiFormSource) && (
        <>
          {tokenUsage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Tokens used:</Typography>
              <Typography variant="body2" fontWeight={600} color="text.secondary">{tokenUsage.total_tokens?.toLocaleString()}</Typography>
              <Typography variant="body2" color="text.disabled">({tokenUsage.prompt_tokens?.toLocaleString()} in · {tokenUsage.output_tokens?.toLocaleString()} out)</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 15, color: 'primary.main' }} />
            <Typography variant="caption" color="primary.main" fontWeight={500}>
              {isEditing ? 'Editing AI-generated test case — changes saved when you click Save Changes' : 'Fields pre-filled by AI — review and edit before creating'}
            </Typography>
          </Box>
        </>
      )}
      {!testSuites?.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {isAdmin ? 'No test suites found. Please create one first.' : 'No test suites found. Ask your project admin to create test suites.'}
        </Alert>
      )}
      {aiFormSource && (
        <AiSuggestions
          suggestedLabels={suggestedLabels}
          suggestedModule={suggestedModule}
          suggestedTechnologyLayer={suggestedTechLayer}
          labels={labels} modules={modules} technologyLayers={technologyLayers}
          onAcceptLabel={handleAcceptLabel}
          onDismissLabel={(id) => setSuggestedLabels((prev) => prev.filter((l) => l !== id))}
          onAcceptModule={handleAcceptModule}
          onDismissModule={() => setSuggestedModule(null)}
          onAcceptTechLayer={handleAcceptTechLayer}
          onDismissTechLayer={() => setSuggestedTechLayer(null)}
        />
      )}
      <CreateTestCaseForm
        formData={formData} isEditing={true} setFormData={setFormData}
        defaultFormData={defaultFormData} projectUsers={projectUsers}
        statuses={filteredStatuses} priorities={priorities} testSuites={testSuites}
        labels={labels} modules={modules} loading={loading}
        isReviewWorkflow={isReviewWorkflow} refetchData={refetchData}
        technologyLayers={technologyLayers}
        isCreateMode={true}
        onAddSteps={mode === 'ai' ? () => setShowStepsModal(true) : undefined}
        stepsCount={aiTestSteps.length}
        createStep={createStep}
        setCreateStep={setCreateStep}
        validateAllAndSubmitRef={validateAllAndSubmitRef}
      />
    </>
  );

  // ── Main content ──────────────────────────────────────────────────────────
  const renderContent = () => {
    if (mode === 'choice') {
      return (
        <ChoiceScreen
          hasAiAccess={hasAiAccess}
          aiAccessDeniedReason={aiAccessDeniedReason}
          onChooseAi={() => setMode('ai')}
          onChooseManual={() => setMode('manual')}
        />
      );
    }

    if (mode === 'manual') return renderFormContent(false);

    if (mode === 'ai') {
      if (aiScreen === 'generating') return <GeneratingOverlay subtitle={genSubtitle} />;

      if (aiScreen === 'input') {
        return (
          <AiInputScreen
            textInput={textInput}
            setTextInput={setTextInput}
            aiError={aiError}
            quantity={aiQuantity}
            onQuantityChange={setAiQuantity}
            testSuites={testSuites}
            selectedTestSuites={selectedTestSuites}
            onTestSuitesChange={setSelectedTestSuites}
            sourceMode={sourceMode}
            onSourceModeChange={(m) => { setSourceMode(m); setAiError(''); }}
            currentProject={currentProject}
            linkedJiraProjects={linkedJiraProjects}
            onPayloadChange={setAiPayload}
          />
        );
      }

      if (aiScreen === 'review') {
        return (
          <JiraReviewScreen
            tcs={reviewTCs}
            onTcsChange={setReviewTCs}
            statuses={statuses} priorities={priorities}
            technologyLayers={technologyLayers} modules={modules} labels={labels}
            onEdit={handleEditTc}
            savePhase={savePhase} saveProgress={saveProgress} saveResult={saveResult}
            existingMatches={existingMatches}
            selectedExisting={selectedExisting}
            onExistingToggle={(id) => setSelectedExisting((prev) => {
              const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
            })}
            selectedStory={selectedStoryRef.current}
            tokenUsage={tokenUsage}
          />
        );
      }

      if (aiScreen === 'edit') return renderFormContent(isEditingTc);
    }

    return null;
  };

  const showStepper = mode !== 'choice' && aiScreen !== 'generating';

  return (
    <>
      <Modal
        open={showModal && !modalHidden}
        handleClose={handleCloseModal}
        modalTitle={modalTitle}
        action={renderActions}
        headerContent={showStepper ? <WizardStepper steps={stepperSteps} activeStep={stepperActive} /> : null}
      >
        {renderContent()}
      </Modal>

      <InMemoryStepsModal
        show={showStepsModal}
        handleClose={() => setShowStepsModal(false)}
        steps={aiTestSteps}
        onSave={(steps) => setAiTestSteps(steps)}
        technologyLayer={currentTechLayerName}
      />

      <ConfirmationModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleDiscard}
        title="Discard Changes"
        body={
          confirmContext === 'edit'
            ? 'Are you sure you want to cancel? Your edits to this test case will be lost.'
            : confirmContext === 'review'
              ? 'Are you sure you want to go back? All generated test cases will be discarded.'
              : 'Are you sure you want to cancel? Any unsaved changes will be lost.'
        }
        buttonLabel="Discard"
        buttonLabelSecondary="Keep Editing"
        buttonStyle="primary"
      />
      <AiFeedbackModal
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        projectId={projectId}
        agentType="TEST_CASE_GENERATOR"
        entityType="TEST_CASE"
        entityIds={feedbackTcIds}
      />
    </>
  );
};

export default AddTestCaseModal;